import { supabase } from '@/lib/supabase';
import { AnalyticsTimeRange, getAnalyticsDateRange } from '@/services/analyticsMetrics';

export type CreatorAnalyticsRange = AnalyticsTimeRange;

// Agents that are both supported by seconds-api redirects and selectable on the
// public sheet (they have per-card affiliate links). Overrides only make sense
// for this intersection, so the admin editor exposes exactly these.
export const OVERRIDE_AGENTS = [
  'kakobuy',
  'oopbuy',
  'lovegobuy',
  'superbuy',
  'mulebuy',
  'joyagoo',
  'cnfans',
  'eastmallbuy',
  'orientdig',
  'loongbuy',
  'basetao',
  'itaobuy',
  'cssbuy',
  'sugargoo',
  'hoobuy',
  'ponybuy',
  'hipobuy',
  'litbuy',
  'gtbuy',
  'cnshopper',
  'mycnbox',
  'bbdbuy',
  'fansbuy',
  'pingubuy',
  'fishgoo',
] as const;

export type OverrideAgent = (typeof OVERRIDE_AGENTS)[number];

export const AFFILIATE_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export interface AgentOverride {
  code: string;
  enabled: boolean;
}

export type AgentOverrides = Record<string, AgentOverride>;

export interface CreatorCode {
  id: string;
  code: string;
  label: string | null;
  enabled: boolean;
  preferredAgent: string | null;
  agentOverrides: AgentOverrides;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreatorSummaryRow {
  code: string;
  label: string | null;
  enabled: boolean;
  isRegistered: boolean;
  serverEntrySessions: number;
  serverEntryRequests: number;
  attributedSessions: number;
  pageViews: number;
  productClicks: number;
  affiliateClicks: number;
  agentSelections: number;
  searches: number;
  zeroResultSearches: number;
  filters: number;
}

type RpcError = { code?: string; message?: string };
type RpcResult<T> = { data: T | null; error: RpcError | null };

type CreatorRpcClient = {
  rpc: (name: string, params?: Record<string, unknown>) => Promise<RpcResult<unknown>>;
};

const rpcClient = supabase as unknown as CreatorRpcClient;

function createCreatorError(error: unknown): Error {
  const errorLike = error as RpcError | null;
  const message = String(errorLike?.message || '');
  const code = String(errorLike?.code || '');
  const lower = message.toLowerCase();

  if (code === '42501' || lower.includes('access denied')) {
    return new Error('This account is not authorized for analytics. Ask the owner to add this Supabase user to analytics_admin_users.');
  }
  if (code === '23505' || lower.includes('already exists')) {
    return new Error('That creator code already exists. Choose a different code.');
  }
  if (lower.includes('affiliate code')) {
    return new Error('Affiliate codes must be 1-64 characters using letters, numbers, hyphen or underscore.');
  }
  if (lower.includes('agent')) {
    return new Error('That shopping agent is not supported.');
  }
  if (code === '22023' || lower.includes('invalid creator code')) {
    return new Error('Creator codes must be 3-32 characters using lowercase letters, numbers, hyphen or underscore.');
  }
  if (code === 'P0002' || lower.includes('not found')) {
    return new Error('Creator code not found.');
  }
  if (lower.includes('function') && lower.includes('does not exist')) {
    return new Error('Creator attribution RPCs are not available in this Supabase project yet.');
  }
  return new Error(message || 'Failed to load creator data.');
}

async function callRpc<T>(name: string, params?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpcClient.rpc(name, params);
  if (error) {
    throw createCreatorError(error);
  }
  return data as T;
}

interface CreatorCodeRpcRow {
  id: string;
  code: string;
  label: string | null;
  enabled: boolean;
  preferred_agent?: string | null;
  agent_overrides?: unknown;
  created_at: string | null;
  updated_at: string | null;
}

function normalizeAgentOverrides(value: unknown): AgentOverrides {
  if (!value || typeof value !== 'object') return {};
  const result: AgentOverrides = {};
  for (const [agent, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    result[agent] = {
      code: typeof entry.code === 'string' ? entry.code : '',
      enabled: Boolean(entry.enabled),
    };
  }
  return result;
}

function normalizeCode(row: CreatorCodeRpcRow): CreatorCode {
  return {
    id: row.id,
    code: row.code,
    label: row.label ?? null,
    enabled: Boolean(row.enabled),
    preferredAgent: row.preferred_agent ?? null,
    agentOverrides: normalizeAgentOverrides(row.agent_overrides),
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

export async function listCreatorCodes(): Promise<CreatorCode[]> {
  const rows = await callRpc<CreatorCodeRpcRow[]>('list_creator_codes');
  return (rows || []).map(normalizeCode);
}

export async function createCreatorCode(code: string, label?: string): Promise<CreatorCode> {
  const row = await callRpc<CreatorCodeRpcRow>('create_creator_code', {
    p_code: code,
    p_label: label && label.trim() ? label.trim() : null,
  });
  return normalizeCode(row);
}

export async function updateCreatorCode(
  id: string,
  changes: { label?: string | null; enabled?: boolean },
): Promise<CreatorCode> {
  const params: Record<string, unknown> = { p_id: id };
  if (changes.label !== undefined) {
    params.p_label = changes.label && changes.label.trim() ? changes.label.trim() : '';
  }
  if (changes.enabled !== undefined) {
    params.p_enabled = changes.enabled;
  }
  const row = await callRpc<CreatorCodeRpcRow>('update_creator_code', params);
  return normalizeCode(row);
}

// Persist a creator's preferred default agent and per-agent affiliate overrides.
// Codes are validated + normalized server-side; empty/disabled+codeless entries
// are dropped. Passing null/'' for preferredAgent clears it.
export async function setCreatorAgentConfig(
  id: string,
  preferredAgent: string | null,
  agentOverrides: AgentOverrides,
): Promise<CreatorCode> {
  const cleaned: AgentOverrides = {};
  for (const [agent, override] of Object.entries(agentOverrides || {})) {
    const code = (override?.code ?? '').trim();
    const enabled = Boolean(override?.enabled);
    if (code || enabled) {
      cleaned[agent] = { code, enabled };
    }
  }
  const row = await callRpc<CreatorCodeRpcRow>('set_creator_agent_config', {
    p_id: id,
    p_preferred_agent: preferredAgent && preferredAgent.trim() ? preferredAgent.trim() : null,
    p_agent_overrides: cleaned,
  });
  return normalizeCode(row);
}

interface CreatorSummaryRpcRow {
  code: string;
  label: string | null;
  enabled: boolean;
  is_registered: boolean;
  server_entry_sessions: number | string;
  server_entry_requests: number | string;
  attributed_sessions: number | string;
  page_views: number | string;
  product_clicks: number | string;
  affiliate_clicks: number | string;
  agent_selections: number | string;
  searches: number | string;
  zero_result_searches: number | string;
  filters: number | string;
}

function toNumber(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function getCreatorAnalytics(range: CreatorAnalyticsRange): Promise<CreatorSummaryRow[]> {
  const dateRange = getAnalyticsDateRange(range);
  const rows = await callRpc<CreatorSummaryRpcRow[]>('get_analytics_creator_summary', {
    p_start: dateRange.start,
    p_end: dateRange.end,
  });

  return (rows || []).map((row) => ({
    code: row.code,
    label: row.label ?? null,
    enabled: Boolean(row.enabled),
    isRegistered: Boolean(row.is_registered),
    serverEntrySessions: toNumber(row.server_entry_sessions),
    serverEntryRequests: toNumber(row.server_entry_requests),
    attributedSessions: toNumber(row.attributed_sessions),
    pageViews: toNumber(row.page_views),
    productClicks: toNumber(row.product_clicks),
    affiliateClicks: toNumber(row.affiliate_clicks),
    agentSelections: toNumber(row.agent_selections),
    searches: toNumber(row.searches),
    zeroResultSearches: toNumber(row.zero_result_searches),
    filters: toNumber(row.filters),
  }));
}

export const CREATOR_CODE_PATTERN = /^[a-z0-9_-]{3,32}$/;

export function buildCreatorShareLink(code: string): string {
  return `https://aliofficials.com/c/${encodeURIComponent(code)}`;
}
